import { format, parseISO, isValid, differenceInDays, addDays } from 'date-fns';

export const formatDate = (date: string | Date, fmt = 'dd MMM yyyy'): string => {
  const d = typeof date === 'string' ? parseISO(date) : date;
  return isValid(d) ? format(d, fmt) : '-';
};

export const formatDateTime = (date: string | Date): string =>
  formatDate(date, 'dd MMM yyyy HH:mm');

export const formatMonthYear = (date: string | Date): string =>
  formatDate(date, 'MMMM yyyy');

export const formatShortDayMonth = (date: string | Date): string =>
  formatDate(date, 'dd-MM');

export const formatHolidayCarouselDate = (date: string | Date): string =>
  formatDate(date, 'EEE, do MMMM yyyy');

export const daysBetween = (from: string | Date, to: string | Date): number => {
  const f = typeof from === 'string' ? parseISO(from) : from;
  const t = typeof to === 'string' ? parseISO(to) : to;
  return differenceInDays(t, f) + 1;
};

export const getInitials = (name: string): string =>
  name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);

export const getDayOfWeek = (date: string | Date): string =>
  formatDate(date, 'EEEE');

/** Normalize API holiday dates to yyyy-MM-dd for calendar matching. */
export const toHolidayDateKey = (value: unknown): string | null => {
  if (value == null) return null;

  if (typeof value === 'string') {
    const match = value.match(/^(\d{4}-\d{2}-\d{2})/);
    return match ? match[1] : null;
  }

  if (typeof value === 'object' && 'year' in value && 'month' in value && 'day' in value) {
    const { year, month, day } = value as { year: number; month: number; day: number };
    return `${year}-${`${month}`.padStart(2, '0')}-${`${day}`.padStart(2, '0')}`;
  }

  return null;
};

/** Normalize API date values (string, ISO, or { year, month, day }) to yyyy-MM-dd. */
export const normalizeToDateKey = (value: unknown): string | null => {
  if (value == null) return null;

  if (typeof value === 'string') {
    const isoMatch = value.match(/^(\d{4}-\d{2}-\d{2})/);
    if (isoMatch) return isoMatch[1];
    const parsed = parseISO(value);
    return isValid(parsed) ? format(parsed, 'yyyy-MM-dd') : null;
  }

  if (typeof value === 'object') {
    const record = value as Record<string, unknown>;
    const year = record.year ?? record.Year;
    const month = record.month ?? record.Month;
    const day = record.day ?? record.Day;
    if (typeof year === 'number' && typeof month === 'number' && typeof day === 'number') {
      return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    }
  }

  return null;
};

export const toLocalDateKey = (date: Date): string => {
  const month = `${date.getMonth() + 1}`.padStart(2, '0');
  const day = `${date.getDate()}`.padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
};

/** Latest yyyy-MM-dd allowed for birth/anniversary fields (yesterday in local time). */
export const getLatestAllowedPastDateKey = (): string => {
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);
  return toLocalDateKey(yesterday);
};

/** True when the date is today or in the future (invalid for birth/anniversary). */
export const isDateTodayOrFuture = (dateStr: string | null | undefined): boolean => {
  const normalized = normalizeToDateKey(dateStr);
  if (!normalized) return false;

  return normalized >= toLocalDateKey(new Date());
};
